import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export const useFirebaseService = (service) => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load data from Firebase
  const loadData = async () => {
    if (!user || !service) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await service.getAll(user.uid);
      setData(result);
    } catch (err) {
      setError(err.message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create new item
  const create = async (itemData) => {
    if (!user || !service) return null;
    
    try {
      const result = await service.create(user.uid, itemData);
      setData(prev => [...prev, result]);
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Error creating item:', err);
      throw err;
    }
  };

  // Update existing item
  const update = async (id, updates) => {
    if (!user || !service) return null;
    
    try {
      const result = await service.update(user.uid, id, updates);
      setData(prev => prev.map(item => item.id === id ? result : item));
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Error updating item:', err);
      throw err;
    }
  };

  // Delete item
  const deleteItem = async (id) => {
    if (!user || !service) return;
    
    try {
      await service.delete(user.uid, id);
      setData(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      setError(err.message);
      console.error('Error deleting item:', err);
      throw err;
    }
  };

  // Get all items
  const getAll = async () => {
    if (!user || !service) return [];
    
    try {
      return await service.getAll(user.uid);
    } catch (err) {
      setError(err.message);
      console.error('Error getting all items:', err);
      return [];
    }
  };

  // Get single item
  const getById = async (id) => {
    if (!user || !service) return null;
    
    try {
      return await service.getById(user.uid, id);
    } catch (err) {
      setError(err.message);
      console.error('Error getting item by id:', err);
      return null;
    }
  };

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadData();
    } else {
      setData([]);
    }
  }, [user]);

  return {
    data,
    loading,
    error,
    create,
    update,
    delete: deleteItem,
    getAll,
    getById,
    refresh: loadData
  };
};
